import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WidgetCard } from './WidgetCard';
import { Widget } from '../types/widget';
import {
  BranchTrendRow,
  fetchStorePerformanceAverages,
  fetchStorePerformanceBranchTrend,
  fetchStorePerformanceForecast,
  fetchStorePerformanceMonths,
  fetchStorePerformanceNewStores,
  fetchStorePerformanceRows,
  fetchStorePerformanceSegmentAverages,
  FinancialSummaryData,
  NewStoresReportRow,
  SegmentSummaryData,
  StoreForecastRow,
  StorePerformanceAveragesResponse,
  StorePerformanceNewStoresResponse,
  StorePerformanceSegmentResponse,
  StorePerformanceRow,
} from '../services/storePerformanceService';

export type StoreReportTab = 'store-performance' | 'segment-report' | 'forecast-report' | 'new-stores-report';
type StorePerformanceView = 'pl' | 'averages' | 'ratios' | 'all-stores';
type ForecastReportView = 'actual' | 'projected';
type NewStoresReportView = 'turnover' | 'rent' | 'rtto';

export interface StorePerformanceContext {
  report_family: 'store-performance';
  active_tab: StoreReportTab;
  report_view: string;
  report_month: string;
  branch_code?: string;
  mall_type?: string;
  province?: string;
}

interface StorePerformanceDashboardProps {
  activeTab: StoreReportTab;
  onAskMore?: (contextQuery: string, contextData?: any) => void;
  onContextChange?: (context: StorePerformanceContext) => void;
}

interface FinancialSummary {
  stores: number;
  target: number | null;
  turnover: number | null;
  turnoverPerSqm: number | null;
  rent: number | null;
  rentPerSqm: number | null;
  salaries: number | null;
  wages: number | null;
  utilities: number | null;
  fixedCharges: number | null;
  variableCharges: number | null;
  totalCheque: number | null;
  profitLoss: number | null;
  transactions: number | null;
  tradeDensity: number | null;
}

interface ForecastParameters {
  turnover: string;
  interestIncome: string;
  badDebts: string;
  rent: string;
  utilities: string;
  otherCosts: string;
  salaries: string;
  wages: string;
  hoCosts: string;
  depreciation: string;
  hoSalaries: string;
}

interface SummaryTableRow {
  Metric: string;
  Value: unknown;
}

const LOADING_VALUE = '__STORE_PERFORMANCE_LOADING__';

const fallbackMonthOptions = [
  '2024-09',
  '2024-08',
  '2024-07',
  '2024-06',
  '2024-05',
  '2024-04',
  '2024-03',
  '2024-02',
  '2024-01',
  '2023-12',
  '2023-11',
  '2023-10',
  '2023-09',
  '2023-08',
];

const performanceViews: Array<[StorePerformanceView, string]> = [
  ['pl', 'P&L'],
  ['averages', 'P&L Averages and Totals'],
  ['ratios', 'Ratios'],
  ['all-stores', 'All Stores Data'],
];

const forecastViews: Array<[ForecastReportView, string]> = [
  ['actual', 'Actual'],
  ['projected', 'Projected'],
];

const newStoreViews: Array<[NewStoresReportView, string]> = [
  ['turnover', 'Store Turnover'],
  ['rent', 'Rent Paid'],
  ['rtto', 'RTTO %'],
];

const VisualLoadingCard: React.FC<{ title?: string }> = ({ title }) => (
  <div className="flex h-full min-h-[120px] flex-col rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
    <div className="mb-4 h-4 w-44 animate-pulse rounded bg-slate-200 dark:bg-slate-700">
      <span className="sr-only">{title ? `Loading ${title}` : 'Loading visual'}</span>
    </div>
    <div className="flex min-h-24 flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600 dark:border-blue-900 dark:border-t-blue-300" />
        <span className="text-xs font-semibold uppercase tracking-wide">Loading data</span>
      </div>
    </div>
  </div>
);

const reportColumns: Array<[keyof StorePerformanceRow, string]> = [
  ['date', 'Date'],
  ['branch_code', 'Branch Code'],
  ['branch_name', 'Branch Name'],
  ['province', 'Province'],
  ['district', 'District'],
  ['mall_type', 'Mall Type'],
  ['type', 'Type'],
  ['status', 'Status'],
  ['rage_sqm', 'Rage SQM'],
  ['target_ex_vat', 'Target (Ex. VAT)'],
  ['turnover_ex_vat', 'Turnover (Ex. VAT)'],
  ['turnover_per_sqm', 'Turnover per SQM'],
  ['rent_ex_vat', 'Rent (Ex. VAT)'],
  ['rent_per_sqm', 'Rent per SQM'],
  ['salaries', 'Salaries'],
  ['wages', 'Wages'],
  ['utilities_ex_vat', 'Utilities (Ex. VAT)'],
  ['fixed_charges_ex_vat', 'Fixed Charges (Ex. VAT)'],
  ['variable_charges_ex_vat', 'Variable Charges (Ex. VAT)'],
  ['total_cheque_paid_ex_vat', 'Total Cheque Paid (Ex. VAT)'],
  ['profit_loss', 'P/L'],
  ['transactions', 'Transactions'],
  ['rtto', 'RTTO'],
  ['r_utto', 'R&UTTO'],
  ['tcotto', 'TCOTTO'],
  ['tctto', 'TCTTO'],
  ['rdttd', 'RDTTD'],
  ['ttsd', 'TTSD'],
  ['rtsd', 'RTSD'],
];

const tabTitles: Record<StoreReportTab, string> = {
  'store-performance': 'Store Performance',
  'segment-report': 'Segment Report',
  'forecast-report': 'Forecast Report - 12 Month Average',
  'new-stores-report': 'New Stores Report',
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const numberInputValue = (value: string): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDivide = (numerator: unknown, denominator: unknown): number | null => {
  const top = toNumber(numerator);
  const bottom = toNumber(denominator);
  if (top === null || bottom === null || bottom === 0) {
    return null;
  }
  return top / bottom;
};

const sumColumn = (rows: StorePerformanceRow[], key: keyof StorePerformanceRow): number | null => {
  let hasValue = false;
  const total = rows.reduce((sum, row) => {
    const value = toNumber(row[key]);
    if (value === null) {
      return sum;
    }
    hasValue = true;
    return sum + value;
  }, 0);
  return hasValue ? total : null;
};

const averageValues = (values: Array<number | null>): number | null => {
  const validValues = values.filter((value): value is number => value !== null);
  if (!validValues.length) {
    return null;
  }
  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
};

const displayValue = (value: unknown): unknown => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return Math.round(value * 100) / 100;
  return value;
};

const performanceDataKeys: Array<keyof StorePerformanceRow> = [
  'target_ex_vat',
  'turnover_ex_vat',
  'rent_ex_vat',
  'salaries',
  'wages',
  'utilities_ex_vat',
  'fixed_charges_ex_vat',
  'variable_charges_ex_vat',
  'total_cheque_paid_ex_vat',
  'profit_loss',
  'transactions',
];

const hasPerformanceData = (row: StorePerformanceRow): boolean =>
  performanceDataKeys.some((key) => toNumber(row[key]) !== null);

const reportRowsForMonth = (rows: StorePerformanceRow[]): StorePerformanceRow[] => {
  const rowsWithPerformanceData = rows.filter(hasPerformanceData);
  return rowsWithPerformanceData.length ? rowsWithPerformanceData : rows;
};

const makeTableRows = (rows: StorePerformanceRow[]) =>
  rows.map((row) =>
    reportColumns.reduce<Record<string, unknown>>((record, [key, label]) => {
      record[label] = displayValue(row[key]);
      return record;
    }, {})
  );

const sumableColumns = new Set<keyof StorePerformanceRow>([
  'rage_sqm', 'target_ex_vat', 'turnover_ex_vat', 'turnover_per_sqm',
  'rent_ex_vat', 'rent_per_sqm', 'salaries', 'wages', 'utilities_ex_vat',
  'fixed_charges_ex_vat', 'variable_charges_ex_vat', 'total_cheque_paid_ex_vat',
  'profit_loss', 'transactions',
]);

const makeAllStoresTotalsRow = (rows: StorePerformanceRow[]): Record<string, unknown> => {
  const totals: Record<string, unknown> = { __IS_TOTALS_ROW__: true };
  for (const [key, label] of reportColumns) {
    if (key === 'date') {
      totals[label] = 'TOTAL';
    } else if (sumableColumns.has(key)) {
      const sum = sumColumn(rows, key);
      totals[label] = sum !== null ? displayValue(sum) : '';
    } else {
      totals[label] = '';
    }
  }
  return totals;
};

const parseMonth = (month: string): Date => {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year, (monthNumber || 1) - 1, 1);
};

const toMonthKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}`;
};

const addMonths = (month: string, offset: number): string => {
  const date = parseMonth(month);
  date.setMonth(date.getMonth() + offset);
  return toMonthKey(date);
};

const formatMonthLabel = (month: string): string =>
  parseMonth(month).toLocaleString('en-US', { month: 'short', year: 'numeric' });

const getTrailingMonths = (reportMonth: string, availableMonths: string[], count: number): string[] => {
  const generated = Array.from({ length: count }, (_, index) => addMonths(reportMonth, index - count + 1));
  const available = new Set(availableMonths);
  const filtered = generated.filter((month) => available.has(month));
  return filtered.length ? filtered : generated;
};

const parseDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const branchKey = (row: StorePerformanceRow): string => String(row.branch_code || '').trim();

const uniqueOptions = (rows: StorePerformanceRow[], key: keyof StorePerformanceRow): string[] => {
  const values = new Set<string>();
  rows.forEach((row) => {
    const value = String(row[key] || '').trim();
    if (value) {
      values.add(value);
    }
  });
  return Array.from(values).sort((left, right) => left.localeCompare(right));
};

const sumNullableValues = (values: Array<number | null>): number | null => {
  const validValues = values.filter((value): value is number => value !== null);
  if (!validValues.length) {
    return null;
  }
  return validValues.reduce((sum, value) => sum + value, 0);
};

const totalChequeForRow = (row: StorePerformanceRow): number | null => {
  return toNumber(row.total_cheque_paid_ex_vat);
};

const totalChequeForRows = (rows: StorePerformanceRow[]): number | null => {
  return sumNullableValues(rows.map(totalChequeForRow));
};

const profitLossForRows = (
  rows: StorePerformanceRow[],
  _gpAdjustmentPercent: number,
  _useActualGp = false,
  _useGpAdjustment = true
): number | null => {
  return sumColumn(rows, 'profit_loss');
};

const makeFinancialSummary = (
  rows: StorePerformanceRow[],
  gpAdjustmentPercent: number,
  useActualGp = false,
  useGpAdjustment = true
): FinancialSummary => {
  const target = sumColumn(rows, 'target_ex_vat');
  const turnover = sumColumn(rows, 'turnover_ex_vat');
  const rageSqm = sumColumn(rows, 'rage_sqm');
  const rent = sumColumn(rows, 'rent_ex_vat');
  const transactions = sumColumn(rows, 'transactions');
  const totalCheque = totalChequeForRows(rows);

  return {
    stores: rows.length,
    target,
    turnover,
    // BI sums the pre-computed per-store column value, not total/aggregate_sqm
    turnoverPerSqm: sumColumn(rows, 'turnover_per_sqm'),
    rent,
    rentPerSqm: sumColumn(rows, 'rent_per_sqm'),
    salaries: sumColumn(rows, 'salaries'),
    wages: sumColumn(rows, 'wages'),
    utilities: sumColumn(rows, 'utilities_ex_vat'),
    fixedCharges: sumColumn(rows, 'fixed_charges_ex_vat'),
    variableCharges: sumColumn(rows, 'variable_charges_ex_vat'),
    totalCheque,
    profitLoss: profitLossForRows(rows, gpAdjustmentPercent, useActualGp, useGpAdjustment),
    transactions,
    // Trade Density is a DAX measure: aggregate turnover / aggregate SQM
    tradeDensity: safeDivide(turnover, rageSqm),
  };
};


const segmentSummaryRows = (summary?: SegmentSummaryData | null) => [
  { Metric: 'Number of Stores', Value: summary?.stores ?? null },
  { Metric: 'Target', Value: summary?.target ?? null },
  { Metric: 'Turnover', Value: summary?.turnover ?? null },
  { Metric: 'Turnover per SQM', Value: summary?.turnover_per_sqm ?? null },
  { Metric: 'Rent', Value: summary?.rent ?? null },
  { Metric: 'Rent per SQM', Value: summary?.rent_per_sqm ?? null },
  { Metric: 'Salaries', Value: summary?.salaries ?? null },
  { Metric: 'Wages', Value: summary?.wages ?? null },
  { Metric: 'Utilities', Value: summary?.utilities ?? null },
  { Metric: 'Fixed Charges', Value: summary?.fixed_charges ?? null },
  { Metric: 'Variable Charges', Value: summary?.variable_charges ?? null },
  { Metric: 'Total Cheque', Value: summary?.total_cheque ?? null },
  { Metric: 'P/L', Value: summary?.profit_loss ?? null },
  { Metric: 'Transactions', Value: summary?.transactions ?? null },
  { Metric: 'Trade Density', Value: summary?.trade_density ?? null },
];

const averageFinancialSummary = (
  monthRows: StorePerformanceRow[][],
  filterRow: (row: StorePerformanceRow) => boolean,
  gpAdjustmentPercent: number,
  useActualGp = false,
  useGpAdjustment = true
): FinancialSummary => {
  const summaries = monthRows
    .map((rows) => makeFinancialSummary(rows.filter(filterRow), gpAdjustmentPercent, useActualGp, useGpAdjustment))
    .filter((summary) => summary.stores > 0);

  if (!summaries.length) {
    return makeFinancialSummary([], gpAdjustmentPercent, useActualGp, useGpAdjustment);
  }

  const averagedSummary: FinancialSummary = {
    stores: averageValues(summaries.map((summary) => summary.stores)) || 0,
    target: averageValues(summaries.map((summary) => summary.target)),
    turnover: averageValues(summaries.map((summary) => summary.turnover)),
    turnoverPerSqm: averageValues(summaries.map((summary) => summary.turnoverPerSqm)),
    rent: averageValues(summaries.map((summary) => summary.rent)),
    rentPerSqm: averageValues(summaries.map((summary) => summary.rentPerSqm)),
    salaries: averageValues(summaries.map((summary) => summary.salaries)),
    wages: averageValues(summaries.map((summary) => summary.wages)),
    utilities: averageValues(summaries.map((summary) => summary.utilities)),
    fixedCharges: averageValues(summaries.map((summary) => summary.fixedCharges)),
    variableCharges: averageValues(summaries.map((summary) => summary.variableCharges)),
    totalCheque: averageValues(summaries.map((summary) => summary.totalCheque)),
    profitLoss: averageValues(summaries.map((summary) => summary.profitLoss)),
    transactions: averageValues(summaries.map((summary) => summary.transactions)),
    tradeDensity: averageValues(summaries.map((summary) => summary.tradeDensity)),
  };
  return averagedSummary;
};

const storeAverageFinancialSummaries = (
  monthRows: StorePerformanceRow[][],
  filterRow: (row: StorePerformanceRow) => boolean,
  gpAdjustmentPercent: number,
  useActualGp = false,
  useGpAdjustment = true
): FinancialSummary[] => {
  const storeCodes = new Set<string>();

  monthRows.forEach((rows) => {
    rows.forEach((row) => {
      const code = branchKey(row);
      if (code && filterRow(row)) {
        storeCodes.add(code);
      }
    });
  });

  return Array.from(storeCodes)
    .sort((left, right) => left.localeCompare(right))
    .map((code) =>
      averageFinancialSummary(
        monthRows,
        (row) => filterRow(row) && branchKey(row) === code,
        gpAdjustmentPercent,
        useActualGp,
        useGpAdjustment
      )
    )
    .filter((summary) => summary.stores > 0);
};

const summarizeStoreAverages = (
  summaries: FinancialSummary[],
  mode: 'sum' | 'average'
): FinancialSummary => {
  const combineValues = mode === 'sum' ? sumNullableValues : averageValues;

  return {
    stores: summaries.length
      ? mode === 'sum'
        ? summaries.reduce((sum, summary) => sum + summary.stores, 0)
        : averageValues(summaries.map((summary) => summary.stores)) || 0
      : 0,
    target: combineValues(summaries.map((summary) => summary.target)),
    turnover: combineValues(summaries.map((summary) => summary.turnover)),
    turnoverPerSqm: averageValues(summaries.map((summary) => summary.turnoverPerSqm)),
    rent: combineValues(summaries.map((summary) => summary.rent)),
    rentPerSqm: averageValues(summaries.map((summary) => summary.rentPerSqm)),
    salaries: combineValues(summaries.map((summary) => summary.salaries)),
    wages: combineValues(summaries.map((summary) => summary.wages)),
    utilities: combineValues(summaries.map((summary) => summary.utilities)),
    fixedCharges: combineValues(summaries.map((summary) => summary.fixedCharges)),
    variableCharges: combineValues(summaries.map((summary) => summary.variableCharges)),
    totalCheque: combineValues(summaries.map((summary) => summary.totalCheque)),
    profitLoss: combineValues(summaries.map((summary) => summary.profitLoss)),
    transactions: combineValues(summaries.map((summary) => summary.transactions)),
    tradeDensity: averageValues(summaries.map((summary) => summary.tradeDensity)),
  };
};

const aggregateRows = (
  rows: StorePerformanceRow[],
  groupKey: keyof StorePerformanceRow,
  groupLabel: string,
  gpAdjustmentPercent = 50,
  useActualGp = false,
  useGpAdjustment = true
) => {
  const grouped = new Map<string, StorePerformanceRow[]>();
  rows.forEach((row) => {
    const groupValue = String(row[groupKey] || 'Unassigned');
    grouped.set(groupValue, [...(grouped.get(groupValue) || []), row]);
  });

  return Array.from(grouped.entries())
    .map(([name, groupRows]) => ({
      [groupLabel]: name,
      Stores: groupRows.length,
      'Rage SQM': sumColumn(groupRows, 'rage_sqm'),
      'Target (Ex. VAT)': sumColumn(groupRows, 'target_ex_vat'),
      'Turnover (Ex. VAT)': sumColumn(groupRows, 'turnover_ex_vat'),
      Transactions: sumColumn(groupRows, 'transactions'),
      'P/L': profitLossForRows(groupRows, gpAdjustmentPercent, useActualGp, useGpAdjustment),
    }))
    .sort((left, right) => String(left[groupLabel]).localeCompare(String(right[groupLabel])));
};

const pnlRows = (
  row?: StorePerformanceRow,
  gpAdjustmentPercent = 50,
  useActualGp = false,
  useGpAdjustment = true,
  loading = false
) => {
  if (loading) {
    return [
      { Metric: 'Target', Value: LOADING_VALUE },
      { Metric: 'Turnover', Value: LOADING_VALUE },
      { Metric: 'Turnover per SQM', Value: LOADING_VALUE },
      { Metric: 'Rent', Value: LOADING_VALUE },
      { Metric: 'Rent per SQM', Value: LOADING_VALUE },
      { Metric: 'Salaries', Value: LOADING_VALUE },
      { Metric: 'Wages', Value: LOADING_VALUE },
      { Metric: 'Utilities', Value: LOADING_VALUE },
      { Metric: 'Fixed Charges', Value: LOADING_VALUE },
      { Metric: 'Variable Charges', Value: LOADING_VALUE },
      { Metric: 'Total Cheque', Value: LOADING_VALUE },
      { Metric: 'P/L', Value: LOADING_VALUE },
      { Metric: 'Transactions', Value: LOADING_VALUE },
    ];
  }
  const rowSet = row ? [row] : [];
  return [
    { Metric: 'Target', Value: row?.target_ex_vat ?? null },
    { Metric: 'Turnover', Value: row?.turnover_ex_vat ?? null },
    { Metric: 'Turnover per SQM', Value: row?.turnover_per_sqm ?? null },
    { Metric: 'Rent', Value: row?.rent_ex_vat ?? null },
    { Metric: 'Rent per SQM', Value: row?.rent_per_sqm ?? null },
    { Metric: 'Salaries', Value: row?.salaries ?? null },
    { Metric: 'Wages', Value: row?.wages ?? null },
    { Metric: 'Utilities', Value: row?.utilities_ex_vat ?? null },
    { Metric: 'Fixed Charges', Value: row?.fixed_charges_ex_vat ?? null },
    { Metric: 'Variable Charges', Value: row?.variable_charges_ex_vat ?? null },
    { Metric: 'Total Cheque', Value: rowSet.length ? totalChequeForRows(rowSet) : null },
    { Metric: 'P/L', Value: rowSet.length ? profitLossForRows(rowSet, gpAdjustmentPercent, useActualGp, useGpAdjustment) : null },
    { Metric: 'Transactions', Value: row?.transactions ?? null },
  ];
};

const ratioDefinitions: Array<{ key: keyof StorePerformanceRow; label: string; meaning: string }> = [
  { key: 'rtto', label: 'RTTO', meaning: 'Ratio of Rent to Turnover' },
  { key: 'r_utto', label: 'R&UTTO', meaning: 'Ratio of Rent and Utilities to Turnover' },
  { key: 'tcotto', label: 'TCOTTO', meaning: 'Ratio of Total Cost of Occupancy to Turnover' },
  { key: 'tctto', label: 'TCTTO', meaning: 'Ratio of Total Cheque Paid to Turnover' },
  { key: 'rdttd', label: 'RDTTD', meaning: 'Ratio of Rental Density to Turnover Density' },
  { key: 'ttsd', label: 'TTSD', meaning: 'Ratio of Turnover to Stock Density' },
  { key: 'rtsd', label: 'RTSD', meaning: 'Ratio of Rent to Stock Density' },
];

const ratioValue = (value: unknown, multiplier = 1): number | null => {
  const numeric = toNumber(value);
  return numeric === null ? null : numeric * multiplier;
};

const ratioMetricValue = (
  row: StorePerformanceRow | undefined,
  key: keyof StorePerformanceRow,
  multiplier = 1
): number | null => {
  if (!row) {
    return null;
  }

  return ratioValue(row[key], multiplier);
};

const formatPercentMetric = (value: unknown, fractionDigits = 2): string | null => {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return `${(numeric * 100).toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;
};

// TTSD/RTSD are not 0-1 fractions like the other ratios (RTTO, R&UTTO, etc.) —
// Power BI displays them as plain numbers, not percentages, so they must
// never receive the ×100 percentage multiplier the others need.
const STOCK_DENSITY_RATIO_KEYS = new Set(['ttsd', 'rtsd']);

const ratioSummaryRows = (rows: Array<StorePerformanceRow | undefined>, multiplier = 1) =>
  ratioDefinitions.map((definition) => {
    const effectiveMultiplier = STOCK_DENSITY_RATIO_KEYS.has(String(definition.key)) ? 1 : multiplier;
    return {
      Metric: definition.label,
      Value: averageValues(rows.map((row) => ratioMetricValue(row, definition.key, effectiveMultiplier))),
    };
  });

const loadingRatioRows = () =>
  ratioDefinitions.map((definition) => ({
    Metric: definition.label,
    Value: LOADING_VALUE,
  }));

const makeWidget = (
  id: string,
  title: string,
  usersQuery: string,
  visualizationType: string,
  data: any[]
): Widget => {
  const createdAt = new Date().toISOString();
  return {
    id,
    user_id: 'report',
    title,
    users_query: usersQuery,
    generated_sql_query: 'Built-in Store Performance report dataset',
    query_response: JSON.stringify(data),
    data,
    last_data_snapshot: data,
    context_history: [],
    visualization_type: visualizationType,
    created_at: createdAt,
    updated_at: createdAt,
  };
};

const metricWidget = (id: string, title: string, query: string, value: number | string | null): Widget =>
  makeWidget(id, title, query, 'metric_card', [{ value: value ?? 'N/A' }]);

const integerMetrics = new Set(['Number of Stores', 'Transactions', 'Stores']);
const percentageMetrics = new Set(['RTTO', 'R&UTTO', 'TCOTTO', 'TCTTO', 'RDTTD', 'Actual GP%']);
// TTSD/RTSD are density ratios, not percentages or currency — Power BI
// displays them as plain rounded numbers (e.g. "1051"), not "%" or "R".
const plainNumberMetrics = new Set(['TTSD', 'RTSD']);

const formatSummaryValue = (metric: string, value: unknown): string => {
  if (value === LOADING_VALUE) {
    return 'Loading...';
  }
  const numeric = toNumber(value);
  if (numeric === null) {
    return 'N/A';
  }

  if (percentageMetrics.has(metric)) {
    return `${numeric.toFixed(2)}%`;
  }

  if (integerMetrics.has(metric) || plainNumberMetrics.has(metric)) {
    return numeric.toLocaleString('en-US', {
      maximumFractionDigits: metric === 'Number of Stores' ? 2 : 0,
    });
  }

  return `R${numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const FinancialSummaryWidget: React.FC<{ widget: Widget }> = ({ widget }) => {
  const rows = ((widget.data || widget.last_data_snapshot || []) as SummaryTableRow[]).filter((row) => row?.Metric);

  return (
    <section className="h-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h3 className="truncate text-sm font-bold text-slate-900 dark:text-slate-100" title={widget.title}>
          {widget.title}
        </h3>
      </div>
      <div className="divide-y divide-slate-200 dark:divide-slate-700">
        {rows.map((row) => {
          const isLoadingValue = row.Value === LOADING_VALUE;
          const profitValue = row.Metric === 'P/L' && !isLoadingValue ? toNumber(row.Value) : null;
          const isPositiveProfit = profitValue !== null && profitValue >= 0;
          const isNegativeProfit = profitValue !== null && profitValue < 0;
          return (
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(110px,auto)] text-xs" key={row.Metric}>
              <div className="bg-slate-100 px-3 py-2 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {row.Metric}
              </div>
              <div
                className={`px-3 py-2 text-right font-semibold ${
                  isPositiveProfit
                    ? 'bg-emerald-600 text-white'
                    : isNegativeProfit
                      ? 'bg-red-500 text-white'
                    : isLoadingValue
                      ? 'bg-white text-slate-400 dark:bg-slate-900 dark:text-slate-500'
                    : 'bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100'
                }`}
              >
                {formatSummaryValue(row.Metric, row.Value)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// Mirrors the BI store_forecast_12_month_ave DAX measures exactly: Actual
// fields come straight from the backend's rolling-average allocation;
// Projected fields are the backend's pre-scaled "_projected" counterparts
// (each Actual value × (100 + slider%) / 100, with GP Adjustment held at
// its Actual value per the BI Profit_Before_Adjustments_Projected measure).
const formatCurrencyValue = (value: unknown): string => {
  const numeric = toNumber(value);
  if (numeric === null) {
    return '';
  }
  return `R${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatForecastNumberValue = (value: unknown): string => {
  const numeric = toNumber(value);
  if (numeric === null) {
    return 'N/A';
  }
  return numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const makeForecastRows = (rows: StoreForecastRow[], view: ForecastReportView, reportMonth: string) =>
  rows.map((row) => {
    const isProjected = view === 'projected';
    return {
      Date: reportMonth,
      'Branch Code': row.branch_code,
      'Branch Name': row.branch_name,
      SQM: formatForecastNumberValue(row.rage_sqm),
      Turnover: formatCurrencyValue(isProjected ? row.turnover_projected : row.turnover),
      'Actual GP%': formatForecastNumberValue(row.actual_gp_percent),
      'GP Adjustment': formatCurrencyValue(row.gp_adjustment),
      'Interest Income': formatCurrencyValue(isProjected ? row.interest_income_projected : row.interest_income),
      'Bad Debts': formatCurrencyValue(isProjected ? row.bad_debts_projected : row.bad_debts),
      'Profit Before Adjustments': formatCurrencyValue(isProjected ? row.profit_before_adjustments_projected : row.profit_before_adjustments),
      Rent: formatCurrencyValue(isProjected ? row.rent_projected : row.rent),
      Utilities: formatCurrencyValue(isProjected ? row.utilities_projected : row.utilities),
      'Other Costs': formatCurrencyValue(isProjected ? row.other_costs_projected : row.other_costs),
      Salaries: formatCurrencyValue(isProjected ? row.salaries_projected : row.salaries),
      Wages: formatCurrencyValue(isProjected ? row.wages_projected : row.wages),
      'Profit Before Headoffice Costing': formatCurrencyValue(isProjected ? row.profit_before_headoffice_costing_projected : row.profit_before_headoffice_costing),
      'HO Costs': formatCurrencyValue(isProjected ? row.ho_costs_projected : row.ho_costs),
      Depreciation: formatCurrencyValue(isProjected ? row.depreciation_projected : row.depreciation),
      'HO Salaries': formatCurrencyValue(isProjected ? row.ho_salaries_projected : row.ho_salaries),
      'Profit After Headoffice Costing': formatCurrencyValue(isProjected ? row.profit_after_headoffice_costing_projected : row.profit_after_headoffice_costing),
      'Trade Density': formatCurrencyValue(isProjected ? row.trade_density_projected : row.trade_density),
    };
  });

const formatNewStoreMetricValue = (value: unknown, view: NewStoresReportView): string => {
  const numeric = toNumber(value);
  if (numeric === null) {
    return '';
  }
  if (view === 'rtto') {
    return `${(numeric * 100).toFixed(2)}%`;
  }
  return formatCurrencyValue(numeric);
};

const filterNewStoreRows = (
  rows: NewStoresReportRow[],
  selectedBranch: string
): NewStoresReportRow[] => {
  if (selectedBranch === 'All') {
    return rows;
  }
  return rows.filter((row) => String(row.branch_code || '').trim() === selectedBranch);
};

const makeNewStoreMovementRows = (
  data: StorePerformanceNewStoresResponse | null,
  view: NewStoresReportView,
  selectedBranch: string
) => {
  if (!data) {
    return [];
  }
  return filterNewStoreRows(data.rows || [], selectedBranch).map((row) => {
    const record: Record<string, unknown> = { 'Branch Code': row.branch_code };
    data.months.forEach((month) => {
      record[month.label] = formatNewStoreMetricValue(row.values?.[month.key], view);
    });
    record.Total = formatNewStoreMetricValue(row.total, view);
    return record;
  });
};

const makeNewStoreTrendRows = (
  data: StorePerformanceNewStoresResponse | null,
  view: NewStoresReportView,
  selectedBranch: string
) => {
  if (!data) {
    return [];
  }
  const rows = filterNewStoreRows(data.rows || [], selectedBranch);
  return data.months.map((month) => {
    const record: Record<string, unknown> = { Date: month.label };
    rows.forEach((row) => {
      const rawValue = toNumber(row.values?.[month.key]);
      record[row.branch_code] = rawValue === null ? null : view === 'rtto' ? rawValue * 100 : rawValue;
    });
    return record;
  });
};

const StorePerformanceDashboard: React.FC<StorePerformanceDashboardProps> = ({
  activeTab,
  onAskMore,
  onContextChange,
}) => {
  const [reportMonth, setReportMonth] = useState('2024-09');
  const [monthsReady, setMonthsReady] = useState(false);
  const [availableMonths, setAvailableMonths] = useState<string[]>(fallbackMonthOptions);
  const [reportView, setReportView] = useState<StorePerformanceView>('pl');
  const [forecastView, setForecastView] = useState<ForecastReportView>('actual');
  const [newStoresView, setNewStoresView] = useState<NewStoresReportView>('turnover');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedMallType, setSelectedMallType] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedNewStoreBranch, setSelectedNewStoreBranch] = useState('All');
  const [gpAdjustmentPercent, setGpAdjustmentPercent] = useState(50);
  const [gpAdjustmentDraft, setGpAdjustmentDraft] = useState('50');
  const [useActualGp, setUseActualGp] = useState(true);
  const [useGpAdjustment, setUseGpAdjustment] = useState(false);
  const [forecastParameters, setForecastParameters] = useState<ForecastParameters>({
    turnover: '20.00',
    interestIncome: '5.00',
    badDebts: '10.50',
    rent: '0.00',
    utilities: '',
    otherCosts: '',
    salaries: '',
    wages: '',
    hoCosts: '',
    depreciation: '',
    hoSalaries: '',
  });
  const [rows, setRows] = useState<StorePerformanceRow[]>([]);
  const [rowsDataKey, setRowsDataKey] = useState('');
  const [historyRowsByMonth, setHistoryRowsByMonth] = useState<Record<string, StorePerformanceRow[]>>({});
  const [historyLoadingByMonth, setHistoryLoadingByMonth] = useState<Record<string, boolean>>({});
  const historyRowsByMonthRef = useRef<Record<string, StorePerformanceRow[]>>({});
  useEffect(() => {
    historyRowsByMonthRef.current = historyRowsByMonth;
  }, [historyRowsByMonth]);
  const [averagesData, setAveragesData] = useState<StorePerformanceAveragesResponse | null>(null);
  const [averagesLoading, setAveragesLoading] = useState(false);
  const [segmentData, setSegmentData] = useState<StorePerformanceSegmentResponse | null>(null);
  const [segmentDataLoading, setSegmentDataLoading] = useState(false);
  const [forecastData, setForecastData] = useState<StoreForecastRow[]>([]);
  const [forecastDataLoading, setForecastDataLoading] = useState(false);
  const [newStoresData, setNewStoresData] = useState<StorePerformanceNewStoresResponse | null>(null);
  const [newStoresLoading, setNewStoresLoading] = useState(false);
  const [branchTrend, setBranchTrend] = useState<BranchTrendRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveGpAdjustment = useGpAdjustment ? gpAdjustmentPercent : 0;
  const includeStockMetrics = reportView === 'ratios' || reportView === 'all-stores';
  const calculationOptions = useMemo(
    () => ({
      gpAdjustmentPercent,
      useActualGp,
      useGpAdjustment,
      includeStockMetrics,
    }),
    [gpAdjustmentPercent, includeStockMetrics, useActualGp, useGpAdjustment]
  );
  const calculationKey = `${gpAdjustmentPercent}|${useActualGp}|${useGpAdjustment}|${includeStockMetrics}`;
  const activeHistoryCalculationKeyRef = useRef(calculationKey);
  const rowsRequestKey = `${reportMonth}|${calculationKey}`;
  const commitGpAdjustment = () => {
    const parsed = Number(gpAdjustmentDraft);
    const nextValue = Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : 50;
    setGpAdjustmentDraft(String(nextValue));
    setGpAdjustmentPercent(nextValue);
  };
  const trailingTwelveMonths = useMemo(
    () => getTrailingMonths(reportMonth, availableMonths, 12),
    [availableMonths, reportMonth]
  );
  const loadedTwelveMonths = useMemo(
    () => trailingTwelveMonths.filter((month) => month === reportMonth || Boolean(historyRowsByMonth[month])),
    [historyRowsByMonth, reportMonth, trailingTwelveMonths]
  );
  const currentReportRows = useMemo(
    () => (rowsDataKey === rowsRequestKey ? reportRowsForMonth(rows) : []),
    [rows, rowsDataKey, rowsRequestKey]
  );
  const requiredHistoryMonths = useMemo(() => {
    const availableMonthSet = new Set(availableMonths);
    const required = new Set<string>();
    const addRequiredMonth = (month: string) => {
      if (month === reportMonth) {
        return;
      }
      if (!availableMonthSet.size || availableMonthSet.has(month)) {
        required.add(month);
      }
    };

    // segment-report and forecast-report deliberately do NOT fetch
    // trailingTwelveMonths: segment-report's summary cards come from the
    // dedicated /segment-averages backend endpoint (which does its own
    // server-side rolling computation), and forecast-report's widgets only
    // read currentReportRows. Neither reads historyRowsByMonth, so fetching
    // 12 months of full row dumps (5000 rows each) for them was pure wasted
    // network/backend load.
    if (activeTab === 'store-performance') {
      if (reportView === 'pl' || reportView === 'ratios') {
        [1, 2, 3].forEach((offset) => addRequiredMonth(addMonths(reportMonth, -offset)));
      }
    }

    return Array.from(required);
  }, [activeTab, availableMonths, reportMonth, reportView]);

  useEffect(() => {
    let cancelled = false;

    const loadMonths = async () => {
      try {
        const months = await fetchStorePerformanceMonths();
        if (cancelled || !months.length) {
          return;
        }
        setAvailableMonths(months);
        setReportMonth((current) => {
          if (months.includes(current)) {
            return current;
          }
          return months.includes('2024-09') ? '2024-09' : months[0];
        });
      } catch (loadError) {
        console.warn('Unable to load Store Performance months', loadError);
      } finally {
        if (!cancelled) {
          setMonthsReady(true);
        }
      }
    };

    loadMonths();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      if (!monthsReady) {
        setIsLoading(true);
        return;
      }
      if (activeTab === 'new-stores-report') {
        setIsLoading(false);
        return;
      }
      const requestKey = rowsRequestKey;
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchStorePerformanceRows(5000, reportMonth, calculationOptions);
        if (!cancelled) {
          setRows(data);
          setRowsDataKey(requestKey);
          setHistoryRowsByMonth((current) => ({ ...current, [reportMonth]: data }));
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load Store Performance data');
          setRows([]);
          setRowsDataKey(requestKey);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadRows();
    return () => {
      cancelled = true;
    };
  }, [activeTab, calculationOptions, monthsReady, reportMonth, rowsRequestKey]);

  useEffect(() => {
    setHistoryRowsByMonth({});
    setHistoryLoadingByMonth({});
    activeHistoryCalculationKeyRef.current = calculationKey;
  }, [calculationKey]);

  useEffect(() => {
    const monthsToLoad = requiredHistoryMonths.filter(
      (month) => !historyRowsByMonthRef.current[month] && !historyLoadingByMonth[month]
    );
    if (!monthsToLoad.length) {
      return;
    }

    const requestCalculationKey = calculationKey;
    setHistoryLoadingByMonth((current) => {
      const next = { ...current };
      monthsToLoad.forEach((month) => {
        next[month] = true;
      });
      return next;
    });

    monthsToLoad.forEach((month) => {
      fetchStorePerformanceRows(5000, month, calculationOptions)
        .then((data) => {
          if (activeHistoryCalculationKeyRef.current === requestCalculationKey) {
            setHistoryRowsByMonth((current) => (current[month] ? current : { ...current, [month]: data }));
          }
        })
        .catch((loadError) => {
          console.warn(`Unable to load Store Performance comparison month ${month}`, loadError);
          if (activeHistoryCalculationKeyRef.current === requestCalculationKey) {
            setHistoryRowsByMonth((current) => (current[month] ? current : { ...current, [month]: [] }));
          }
        })
        .finally(() => {
          if (activeHistoryCalculationKeyRef.current === requestCalculationKey) {
            setHistoryLoadingByMonth((current) => {
              if (!current[month]) {
                return current;
              }
              const next = { ...current };
              delete next[month];
              return next;
            });
          }
        });
    });
  }, [calculationKey, calculationOptions, historyLoadingByMonth, requiredHistoryMonths]);

  useEffect(() => {
    if (activeTab !== 'store-performance' || (reportView !== 'averages' && reportView !== 'ratios') || !selectedBranch) {
      setAveragesData(null);
      setAveragesLoading(false);
      return;
    }

    let cancelled = false;
    setAveragesData(null);
    setAveragesLoading(true);

    const loadAverages = async () => {
      try {
        const data = await fetchStorePerformanceAverages(selectedBranch, reportMonth, calculationOptions);
        if (!cancelled) {
          setAveragesData(data);
        }
      } catch (loadError) {
        console.warn('Unable to load Store Performance averages', loadError);
      } finally {
        if (!cancelled) {
          setAveragesLoading(false);
        }
      }
    };

    loadAverages();
    return () => {
      cancelled = true;
    };
  }, [activeTab, calculationOptions, reportMonth, reportView, selectedBranch]);

  useEffect(() => {
    if (activeTab !== 'segment-report') {
      setSegmentData(null);
      setSegmentDataLoading(false);
      return;
    }

    let cancelled = false;
    setSegmentData(null);
    setSegmentDataLoading(true);

    const loadSegment = async () => {
      try {
        const data = await fetchStorePerformanceSegmentAverages(
          reportMonth,
          selectedMallType || undefined,
          selectedProvince || undefined,
          calculationOptions
        );
        if (!cancelled) setSegmentData(data);
      } catch (err) {
        console.warn('Unable to load segment averages', err);
      } finally {
        if (!cancelled) setSegmentDataLoading(false);
      }
    };

    loadSegment();
    return () => { cancelled = true; };
  }, [activeTab, calculationOptions, reportMonth, selectedMallType, selectedProvince]);

  const forecastAdjustments = useMemo(
    () => ({
      turnoverPercent: numberInputValue(forecastParameters.turnover),
      interestIncomePercent: numberInputValue(forecastParameters.interestIncome),
      badDebtsPercent: numberInputValue(forecastParameters.badDebts),
      rentPercent: numberInputValue(forecastParameters.rent),
      utilitiesPercent: numberInputValue(forecastParameters.utilities),
      otherCostsPercent: numberInputValue(forecastParameters.otherCosts),
      salariesPercent: numberInputValue(forecastParameters.salaries),
      wagesPercent: numberInputValue(forecastParameters.wages),
      hoCostsPercent: numberInputValue(forecastParameters.hoCosts),
      depreciationPercent: numberInputValue(forecastParameters.depreciation),
      hoSalariesPercent: numberInputValue(forecastParameters.hoSalaries),
    }),
    [forecastParameters]
  );

  useEffect(() => {
    if (activeTab !== 'forecast-report') {
      setForecastData([]);
      setForecastDataLoading(false);
      return;
    }

    let cancelled = false;
    setForecastData([]);
    setForecastDataLoading(true);

    const loadForecast = async () => {
      try {
        const data = await fetchStorePerformanceForecast(reportMonth, calculationOptions, forecastAdjustments);
        if (!cancelled) setForecastData(data);
      } catch (err) {
        console.warn('Unable to load store forecast', err);
      } finally {
        if (!cancelled) setForecastDataLoading(false);
      }
    };

    loadForecast();
    return () => { cancelled = true; };
  }, [activeTab, calculationOptions, forecastAdjustments, reportMonth]);

  useEffect(() => {
    if (activeTab !== 'new-stores-report') {
      setNewStoresData(null);
      setNewStoresLoading(false);
      return;
    }

    let cancelled = false;
    setNewStoresLoading(true);

    const loadNewStores = async () => {
      try {
        const data = await fetchStorePerformanceNewStores(newStoresView);
        if (!cancelled) {
          setNewStoresData(data);
        }
      } catch (err) {
        console.warn('Unable to load new stores report', err);
        if (!cancelled) {
          setNewStoresData(null);
        }
      } finally {
        if (!cancelled) {
          setNewStoresLoading(false);
        }
      }
    };

    loadNewStores();
    return () => { cancelled = true; };
  }, [activeTab, newStoresView]);

  useEffect(() => {
    if (activeTab !== 'store-performance' || reportView !== 'ratios' || !selectedBranch) {
      setBranchTrend(null);
      return;
    }

    let cancelled = false;

    fetchStorePerformanceBranchTrend(selectedBranch)
      .then((rows) => {
        if (!cancelled) {
          setBranchTrend(rows);
        }
      })
      .catch((loadError) => {
        console.warn('Unable to load Store Performance branch trend', loadError);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, reportView, selectedBranch]);

  const branchOptions = useMemo(() => {
    const seen = new Set<string>();
    return currentReportRows
      .map((row) => ({
        branch_code: String(row.branch_code || '').trim(),
        branch_name: String(row.branch_name || '').trim(),
      }))
      .filter((branch) => {
        if (!branch.branch_code || seen.has(branch.branch_code)) {
          return false;
        }
        seen.add(branch.branch_code);
        return true;
      })
      .sort((left, right) => left.branch_code.localeCompare(right.branch_code));
  }, [currentReportRows]);

  const mallTypeOptions = useMemo(() => uniqueOptions(currentReportRows, 'mall_type'), [currentReportRows]);
  const provinceOptions = useMemo(() => uniqueOptions(currentReportRows, 'province'), [currentReportRows]);
  const newStoreRows = useMemo(() => newStoresData?.rows || [], [newStoresData]);
  const storeRowsPending = activeTab !== 'new-stores-report' && (!monthsReady || rowsDataKey !== rowsRequestKey);
  const showMainLoading = activeTab !== 'new-stores-report' && (isLoading || storeRowsPending);

  useEffect(() => {
    if (storeRowsPending) {
      return;
    }
    if (!branchOptions.length) {
      setSelectedBranch('');
      return;
    }
    setSelectedBranch((current) => {
      if (current && branchOptions.some((branch) => branch.branch_code === current)) {
        return current;
      }
      return branchOptions.find((branch) => branch.branch_code === 'OMP')?.branch_code || branchOptions[0].branch_code;
    });
  }, [branchOptions, storeRowsPending]);

  useEffect(() => {
    if (storeRowsPending) {
      return;
    }
    setSelectedMallType((current) => {
      if (current && mallTypeOptions.includes(current)) {
        return current;
      }
      return mallTypeOptions.find((value) => value === 'Metropolitan - Mall') || mallTypeOptions[0] || '';
    });
  }, [mallTypeOptions, storeRowsPending]);

  useEffect(() => {
    if (storeRowsPending) {
      return;
    }
    setSelectedProvince((current) => {
      if (current && provinceOptions.includes(current)) {
        return current;
      }
      return provinceOptions.find((value) => value === 'Eastern Cape') || provinceOptions[0] || '';
    });
  }, [provinceOptions, storeRowsPending]);

  useEffect(() => {
    setSelectedNewStoreBranch((current) => {
      if (current === 'All') {
        return current;
      }
      return newStoreRows.some((row) => String(row.branch_code || '').trim() === current) ? current : 'All';
    });
  }, [newStoreRows]);

  const selectedStore = useMemo(
    () => currentReportRows.find((row) => branchKey(row) === selectedBranch),
    [currentReportRows, selectedBranch]
  );
  useEffect(() => {
    onContextChange?.({
      report_family: 'store-performance',
      active_tab: activeTab,
      report_view: activeTab === 'store-performance'
        ? reportView
        : activeTab === 'forecast-report'
          ? forecastView
          : activeTab === 'new-stores-report'
            ? newStoresView
            : 'segment',
      report_month: reportMonth,
      branch_code: activeTab === 'new-stores-report' ? selectedNewStoreBranch : selectedBranch || undefined,
      mall_type: selectedMallType || undefined,
      province: selectedProvince || undefined,
    });
  }, [
    activeTab,
    forecastView,
    newStoresView,
    onContextChange,
    reportMonth,
    reportView,
    selectedBranch,
    selectedMallType,
    selectedNewStoreBranch,
    selectedProvince,
  ]);

  const widgets = useMemo(() => {
    const targetTotal = sumColumn(currentReportRows, 'target_ex_vat');
    const turnoverTotal = sumColumn(currentReportRows, 'turnover_ex_vat');
    const transactionTotal = sumColumn(currentReportRows, 'transactions');
    const provinceRows = aggregateRows(currentReportRows, 'province', 'Province', effectiveGpAdjustment, useActualGp, useGpAdjustment);
    const mallRows = aggregateRows(currentReportRows, 'mall_type', 'Mall Type', effectiveGpAdjustment, useActualGp, useGpAdjustment);
    const allStoresRows = [...makeTableRows(currentReportRows), makeAllStoresTotalsRow(currentReportRows)];
    const selectedBranchLabel = selectedBranch || 'selected branch';
    const branchRowForMonth = (month: string) => {
      const sourceRows = month === reportMonth ? currentReportRows : reportRowsForMonth(historyRowsByMonth[month] || []);
      return sourceRows.find((row) => branchKey(row) === selectedBranch);
    };
    const isHistoryMonthPending = (month: string) =>
      month !== reportMonth && requiredHistoryMonths.includes(month) && historyRowsByMonth[month] === undefined;

    if (activeTab === 'segment-report') {
      return [
        makeWidget('report-segment-mall-current', 'Mall Type Segment - P&L Current', `Show current P&L for mall type ${selectedMallType}`, 'financial_summary', segmentSummaryRows(segmentData?.mall_type.current)),
        makeWidget('report-segment-mall-average', 'Mall Type Segment - P&L 12 Month Avg', `Show 12 month average P&L for mall type ${selectedMallType}`, 'financial_summary', segmentSummaryRows(segmentData?.mall_type.avg_12m)),
        makeWidget('report-segment-province-current', 'Province Segment - P&L Current', `Show current P&L for province ${selectedProvince}`, 'financial_summary', segmentSummaryRows(segmentData?.province.current)),
        makeWidget('report-segment-province-average', 'Province Segment - P&L 12 Month Avg', `Show 12 month average P&L for province ${selectedProvince}`, 'financial_summary', segmentSummaryRows(segmentData?.province.avg_12m)),
        makeWidget('report-segment-mall-distribution', 'Mall Type Distribution', 'Show Store Performance by mall type', 'bar_chart', mallRows),
        makeWidget('report-segment-province-distribution', 'Province Distribution', 'Show Store Performance by province', 'bar_chart', provinceRows),
      ];
    }

    if (activeTab === 'forecast-report') {
      const forecastRows = makeForecastRows(forecastData, forecastView, reportMonth);
      return [
        makeWidget(
          'report-forecast-table',
          `${forecastView === 'actual' ? 'Actual' : 'Projected'} Forecast Data`,
          'Show forecast report data',
          'table',
          forecastRows
        ),
      ];
    }

    if (activeTab === 'new-stores-report') {
      const movementRows = makeNewStoreMovementRows(newStoresData, newStoresView, selectedNewStoreBranch);
      const trendRows = makeNewStoreTrendRows(newStoresData, newStoresView, selectedNewStoreBranch);
      const metricLabel = newStoresView === 'turnover' ? 'Store Turnover' : newStoresView === 'rent' ? 'Rent Paid' : 'RTTO %';

      return [
        makeWidget('report-new-stores-table', `${metricLabel} (Last 4 Months)`, `Show ${metricLabel} for new stores by month`, 'table', movementRows),
        makeWidget('report-new-stores-movement', 'Movement', `Show ${metricLabel} movement for new stores`, 'line_chart', trendRows),
      ];
    }

    if (reportView === 'averages') {
      const b3m = averagesData?.branch_3m;
      const b12m = averagesData?.branch_12m;
      const provAvg = averagesData?.province_avg;
      const allAvg = averagesData?.all_stores_avg;
      const allTotalTurnover = averagesData?.all_stores_total_turnover ?? null;
      const allTotalProfitLoss = averagesData?.all_stores_total_profit_loss ?? null;
      const selectedProvinceName = averagesData?.province_name || String(selectedStore?.province || '').trim();

      const currentBranchSummary = makeFinancialSummary(
        selectedStore ? [selectedStore] : [],
        effectiveGpAdjustment,
        useActualGp,
        useGpAdjustment
      );
      const turnoverWeighting = safeDivide(currentBranchSummary.turnover, allTotalTurnover);
      const profitLossWeighting = safeDivide(currentBranchSummary.profitLoss, allTotalProfitLoss);

      const averageSummaryRows = (summary?: FinancialSummaryData | null) => [
        { Metric: 'Target', Value: summary?.target ?? null },
        { Metric: 'Turnover', Value: summary?.turnover ?? null },
        { Metric: 'Turnover per SQM', Value: summary?.turnover_per_sqm ?? null },
        { Metric: 'Rent', Value: summary?.rent ?? null },
        { Metric: 'Rent per SQM', Value: summary?.rent_per_sqm ?? null },
        { Metric: 'Salaries', Value: summary?.salaries ?? null },
        { Metric: 'Wages', Value: summary?.wages ?? null },
        { Metric: 'Utilities', Value: summary?.utilities ?? null },
        { Metric: 'Fixed Charges', Value: summary?.fixed_charges ?? null },
        { Metric: 'Variable Charges', Value: summary?.variable_charges ?? null },
        { Metric: 'Total Cheque', Value: summary?.total_cheque ?? null },
        { Metric: 'P/L', Value: summary?.profit_loss ?? null },
        { Metric: 'Transactions', Value: summary?.transactions ?? null },
      ];

      return [
        metricWidget('report-avg-turnover-all', 'Turnover All Stores (Sum 12 month avg.)', 'What is the sum of 12 month average turnover for all stores?', allTotalTurnover),
        metricWidget('report-avg-pl-all', 'P/L All Stores (Sum 12 month avg.)', 'What is the sum of 12 month average P/L for all stores?', allTotalProfitLoss),
        metricWidget('report-avg-turnover-weight', 'Store Weighting on Turnover', `What is ${selectedBranchLabel} weighting on 12 month average turnover?`, formatPercentMetric(turnoverWeighting, 4)),
        metricWidget('report-avg-pl-weight', 'Store Weighting on P/L', `What is ${selectedBranchLabel} weighting on 12 month average P/L?`, formatPercentMetric(profitLossWeighting, 4)),
        makeWidget('report-avg-3m', 'P&L 3 Month Avg', `Show 3 month average P&L for ${selectedBranchLabel}`, 'financial_summary', averageSummaryRows(b3m)),
        makeWidget('report-avg-12m', 'P&L 12 Month Avg', `Show 12 month average P&L for ${selectedBranchLabel}`, 'financial_summary', averageSummaryRows(b12m)),
        makeWidget('report-avg-province-summary', `P&L 12 Month Avg by Province${selectedProvinceName ? ` — ${selectedProvinceName}` : ''}`, `Show 12 month average P&L for province ${selectedProvinceName}`, 'financial_summary', averageSummaryRows(provAvg)),
        makeWidget('report-avg-all-stores', 'P&L 12 Month Avg by All Stores', 'Show 12 month average P&L across all stores', 'financial_summary', averageSummaryRows(allAvg)),
      ];
    }

    if (reportView === 'ratios') {
      const ratioPeriods = [
        { id: 'current', title: 'Current', month: reportMonth },
        { id: 'prior-1', title: '1 Month Prior', month: addMonths(reportMonth, -1) },
        { id: 'prior-2', title: '2 Months Prior', month: addMonths(reportMonth, -2) },
        { id: 'prior-3', title: '3 Months Prior', month: addMonths(reportMonth, -3) },
      ];
      // Mirrors the Power BI semantic model: average-period ratios are derived
      // from the rolling-averaged fields, with nulls propagating like M does.
      const ratiosFromAverages = (summary?: FinancialSummaryData | null) => {
        const pct = (numerator: number | null, denominator: number | null | undefined) =>
          numerator === null || denominator === null || denominator === undefined || !denominator
            ? null
            : (numerator / denominator) * 100;
        const addParts = (...parts: Array<number | null | undefined>) => {
          let total = 0;
          for (const part of parts) {
            if (part === null || part === undefined) {
              return null;
            }
            total += part;
          }
          return total;
        };
        return [
          { Metric: 'RTTO', Value: pct(summary?.rent ?? null, summary?.turnover) },
          { Metric: 'R&UTTO', Value: pct(addParts(summary?.rent, summary?.utilities), summary?.turnover) },
          { Metric: 'TCOTTO', Value: pct(addParts(summary?.rent, summary?.utilities, summary?.fixed_charges, summary?.variable_charges), summary?.turnover) },
          { Metric: 'TCTTO', Value: pct(summary?.total_cheque ?? null, summary?.turnover) },
          { Metric: 'RDTTD', Value: pct(summary?.rent_per_sqm ?? null, summary?.turnover_per_sqm) },
          { Metric: 'TTSD', Value: summary?.ttsd ?? null },
          { Metric: 'RTSD', Value: summary?.rtsd ?? null },
        ];
      };
      const ratioTrendRows = branchTrend?.length
        ? branchTrend.map((row) => ({
            Date: formatMonthLabel(row.date),
            Turnover: toNumber(row.turnover_ex_vat) || 0,
            RTTO: ratioValue(row.rtto, 100) || 0,
            'R&UTTO': ratioValue(row.r_utto, 100) || 0,
            TCOTTO: ratioValue(row.tcotto, 100) || 0,
            TCTTO: ratioValue(row.tctto, 100) || 0,
            RDTTD: ratioValue(row.rdttd, 100) || 0,
          }))
        : [3, 2, 1, 0]
            .map((offset) => addMonths(reportMonth, -offset))
            .filter((month) => month === reportMonth || Boolean(historyRowsByMonth[month]))
            .map((month) => {
              const row = branchRowForMonth(month);
              return {
                Date: formatMonthLabel(month),
                Turnover: toNumber(row?.turnover_ex_vat) || 0,
                RTTO: ratioValue(row?.rtto, 100) || 0,
                'R&UTTO': ratioValue(row?.r_utto, 100) || 0,
                TCOTTO: ratioValue(row?.tcotto, 100) || 0,
                TCTTO: ratioValue(row?.tctto, 100) || 0,
                RDTTD: ratioValue(row?.rdttd, 100) || 0,
              };
            });
      return [
        ...ratioPeriods.map((period) => {
          const periodRow = branchRowForMonth(period.month);
          const periodLoading = isHistoryMonthPending(period.month);
          return makeWidget(
            `report-ratio-${period.id}`,
            period.title,
            `Show ${period.title} ratios for ${selectedBranchLabel}`,
            'financial_summary',
            periodLoading ? loadingRatioRows() : ratioSummaryRows(periodRow ? [periodRow] : [], 100)
          );
        }),
        makeWidget('report-ratio-avg-3', '3 Months Avg', `Show 3 month average ratios for ${selectedBranchLabel}`, 'financial_summary', ratiosFromAverages(averagesData?.branch_3m)),
        makeWidget('report-ratio-avg-12', '12 Months Avg', `Show 12 month average ratios for ${selectedBranchLabel}`, 'financial_summary', ratiosFromAverages(averagesData?.branch_12m)),
        makeWidget('report-ratio-trend', 'Ratios by Date', `Show ratio trend for ${selectedBranchLabel}`, 'combo_chart', ratioTrendRows),
        makeWidget('report-ratio-table', 'Key Legend', 'Show ratio key legend', 'table', ratioDefinitions.map((definition) => ({
          Ratio: definition.label,
          Meaning: definition.meaning,
        }))),
      ];
    }

    if (reportView === 'all-stores') {
      return [
        makeWidget('report-all-stores', 'All Stores Data', 'Show all Store Performance rows', 'table', allStoresRows),
      ];
    }

    const pnlPeriods = [
      { id: 'current', title: 'P&L Current', month: reportMonth },
      { id: 'prior-1', title: 'P&L 1 Month Prior', month: addMonths(reportMonth, -1) },
      { id: 'prior-2', title: 'P&L 2 Months Prior', month: addMonths(reportMonth, -2) },
      { id: 'prior-3', title: 'P&L 3 Months Prior', month: addMonths(reportMonth, -3) },
    ];
    const turnoverProfitTrendRows = loadedTwelveMonths.map((month) => {
      const row = branchRowForMonth(month);
      const rowSet = row ? [row] : [];
      return {
        Date: formatMonthLabel(month),
        Turnover: toNumber(row?.turnover_ex_vat) || 0,
        'P/L': rowSet.length ? profitLossForRows(rowSet, effectiveGpAdjustment, useActualGp, useGpAdjustment) || 0 : 0,
      };
    });

    return [
      ...pnlPeriods.map((period) => {
        const periodLoading = isHistoryMonthPending(period.month);
        return makeWidget(
          `report-branch-pl-${period.id}`,
          period.title,
          `Show ${period.title} for ${selectedBranchLabel}`,
          'financial_summary',
          pnlRows(
            period.id === 'current' ? selectedStore : branchRowForMonth(period.month),
            effectiveGpAdjustment,
            useActualGp,
            useGpAdjustment,
            periodLoading
          )
        );
      }),
      makeWidget('report-branch-turnover-pl-trend', 'Turnover vs. P&L', `Show turnover and P&L trend for ${selectedBranchLabel}`, 'bar_chart', turnoverProfitTrendRows),
    ];
  }, [
    activeTab,
    averagesData,
    branchTrend,
    currentReportRows,
    effectiveGpAdjustment,
    forecastData,
    forecastView,
    historyRowsByMonth,
    loadedTwelveMonths,
    newStoresData,
    newStoreRows,
    newStoresView,
    reportMonth,
    reportView,
    requiredHistoryMonths,
    segmentData,
    selectedBranch,
    selectedMallType,
    selectedNewStoreBranch,
    selectedProvince,
    selectedStore,
    useActualGp,
    useGpAdjustment,
  ]);

  const askMoreWithoutWidgetId = (contextQuery: string, contextData?: any) => {
    if (!onAskMore) {
      return;
    }
    const { id, ...contextWithoutId } = contextData || {};
    onAskMore(contextQuery, contextWithoutId);
  };

  const metricWidgets = widgets.filter((widget) => ['metric_card', 'metric'].includes(widget.visualization_type));
  const summaryWidgets = widgets.filter((widget) => widget.visualization_type === 'financial_summary');
  const chartWidgets = widgets.filter((widget) =>
    !['table', 'financial_summary', 'metric_card', 'metric'].includes(widget.visualization_type)
  );
  const tableWidgets = widgets.filter((widget) => widget.visualization_type === 'table');
  const isWidgetLoading =
    showMainLoading ||
    (reportView === 'averages' && averagesLoading) ||
    (activeTab === 'forecast-report' && forecastDataLoading) ||
    (activeTab === 'new-stores-report' && newStoresLoading);

  const renderWidget = (widget: Widget, className = '') => (
    <div key={widget.id} className={className}>
      {isWidgetLoading ? (
        <VisualLoadingCard title={widget.title} />
      ) : widget.visualization_type === 'financial_summary' ? (
        <FinancialSummaryWidget widget={widget} />
      ) : (
        <WidgetCard
          widget={widget}
          onDelete={() => undefined}
          onAskMore={askMoreWithoutWidgetId}
          hideManagementActions
        />
      )}
    </div>
  );

  const renderGpControls = () => (
    <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">GP Settings</h3>
      </div>
      <div className="space-y-3 p-4">
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          GP Adjustment %
          <input
            type="number"
            value={gpAdjustmentDraft}
            onChange={(event) => setGpAdjustmentDraft(event.target.value)}
            onBlur={commitGpAdjustment}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          />
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={useActualGp} onChange={(event) => { setUseActualGp(event.target.checked); if (event.target.checked) setUseGpAdjustment(false); }} />
          Use Actual GP %
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={useGpAdjustment} onChange={(event) => { setUseGpAdjustment(event.target.checked); if (event.target.checked) setUseActualGp(false); }} />
          Use GP Adjustment %
        </label>
      </div>
    </div>
  );

  const detailFields: Array<[string, unknown]> = [
    ['Branch Code', selectedStore?.branch_code || selectedBranch],
    ['Branch Name', selectedStore?.branch_name],
    ['Province', selectedStore?.province],
    ['District', selectedStore?.district],
    ['Status', selectedStore?.status],
    ['Rage SQM', selectedStore?.rage_sqm],
    ['Type', selectedStore?.type],
    ['Mall Type', selectedStore?.mall_type],
    ['Actual GP%', selectedStore?.actual_gp_percent],
  ];

  const renderBranchControls = () => (
    <>
      <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Branch Code</h3>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {branchOptions.map((branch) => {
            const isSelected = selectedBranch === branch.branch_code;
            return (
              <button
                key={branch.branch_code}
                type="button"
                onClick={() => setSelectedBranch(branch.branch_code)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
                title={branch.branch_name || branch.branch_code}
              >
                <span className={`h-3 w-3 rounded-full border ${isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-400'}`} />
                <span className="font-semibold">{branch.branch_code}</span>
                {branch.branch_name && <span className="min-w-0 flex-1 truncate text-xs opacity-70">{branch.branch_name}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Branch Details</h3>
        </div>
        <dl className="space-y-3 p-4">
          {detailFields.map(([label, value]) => {
            const resolvedValue = displayValue(value);
            return (
              <div key={label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</dt>
                <dd className="mt-0.5 truncate text-sm font-medium text-slate-800 dark:text-slate-100" title={String(resolvedValue)}>
                  {resolvedValue === '' ? '(Blank)' : String(resolvedValue)}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </>
  );

  const renderSegmentControls = () => (
    <>
      {renderGpControls()}
      <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Mall Type</h3>
        </div>
        <div className="max-h-60 overflow-y-auto p-2">
          {mallTypeOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSelectedMallType(option)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                selectedMallType === option
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <span className={`h-3 w-3 rounded-full border ${selectedMallType === option ? 'border-blue-600 bg-blue-600' : 'border-slate-400'}`} />
              <span className="min-w-0 flex-1 truncate">{option}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Province</h3>
        </div>
        <div className="max-h-60 overflow-y-auto p-2">
          {provinceOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSelectedProvince(option)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                selectedProvince === option
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              <span className={`h-3 w-3 rounded-full border ${selectedProvince === option ? 'border-blue-600 bg-blue-600' : 'border-slate-400'}`} />
              <span>{option}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const updateForecastParameter = (key: keyof ForecastParameters, value: string) => {
    setForecastParameters((current) => ({ ...current, [key]: value }));
  };

  const renderForecastControls = () => (
    <>
      {renderGpControls()}
      {forecastView === 'projected' && (
        <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
          <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Projection Inputs</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 p-4">
            {Object.entries({
              turnover: 'Turnover %',
              interestIncome: 'Interest %',
              badDebts: 'Bad Debts %',
              rent: 'Rent %',
              utilities: 'Utilities %',
              otherCosts: 'Other Costs %',
              salaries: 'Salaries %',
              wages: 'Wages %',
              hoCosts: 'HO Costs %',
              depreciation: 'Depreciation %',
              hoSalaries: 'HO Salaries %',
            }).map(([key, label]) => (
              <label key={key} className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
                <input
                  type="number"
                  value={forecastParameters[key as keyof ForecastParameters]}
                  onChange={(event) => updateForecastParameter(key as keyof ForecastParameters, event.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderNewStoresControls = () => (
    <div className="rounded-xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Branch Code</h3>
      </div>
      <div className="p-4">
        <select
          value={selectedNewStoreBranch}
          onChange={(event) => setSelectedNewStoreBranch(event.target.value)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="All">All</option>
          {newStoreRows.map((row) => (
            <option key={row.branch_code} value={row.branch_code}>
              {row.branch_code}
              {row.branch_name ? ` - ${row.branch_name}` : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderAside = () => {
    if (activeTab === 'segment-report') {
      return renderSegmentControls();
    }
    if (activeTab === 'forecast-report') {
      return renderForecastControls();
    }
    if (activeTab === 'new-stores-report') {
      return renderNewStoresControls();
    }
    return (
      <>
        {renderGpControls()}
        {renderBranchControls()}
      </>
    );
  };

  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur-sm dark:border-slate-700/70 dark:bg-slate-900/70 overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-5 dark:border-slate-700 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
            {tabTitles[activeTab]}
          </div>
          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">Store Performance Report</h2>
        </div>
        {activeTab !== 'new-stores-report' && (
          <label className="flex items-center gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
            <span>Date</span>
            <select
              value={reportMonth}
              onChange={(event) => setReportMonth(event.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            >
              {availableMonths.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="grid gap-5 p-5 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="space-y-4 min-w-0">
          {renderAside()}
        </aside>

        <div className="min-w-0 space-y-5">
          {activeTab === 'store-performance' && (
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/60 lg:grid-cols-4">
              {performanceViews.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setReportView(key)}
                  className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                    reportView === key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'forecast-report' && (
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/60">
              {forecastViews.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForecastView(key)}
                  className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                    forecastView === key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'new-stores-report' && (
            <>
              <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/60">
                {newStoreViews.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewStoresView(key)}
                    className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                      newStoresView === key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-white hover:text-blue-700 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-slate-600 dark:text-slate-300">
                This report is based on stores with a first trading date within the last 12 months and shows the {newStoresView === 'turnover' ? 'turnover' : newStoresView === 'rent' ? 'rent paid' : 'RTTO %'} for the last 4 months
              </p>
            </>
          )}

          {showMainLoading && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <VisualLoadingCard key={`metric-loading-${index}`} title="metric" />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={`visual-loading-${index}`} className="h-96">
                    <VisualLoadingCard title="report visual" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showMainLoading && error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}

          {!showMainLoading && !error && reportView === 'averages' && averagesLoading && (
            <div className="space-y-8">
              {/* Metric card skeletons */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="min-h-[120px] rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 flex items-center gap-4 px-5 py-4 animate-pulse">
                    <div className="h-11 w-11 rounded-full bg-blue-100 dark:bg-blue-900/40 shrink-0 flex items-center justify-center">
                      <div className="h-5 w-5 rounded-full bg-blue-200 dark:bg-blue-800" />
                    </div>
                    <div className="flex-1 space-y-2.5">
                      <div className="h-6 w-3/5 rounded-lg bg-slate-200 dark:bg-slate-700" />
                      <div className="h-3 w-4/5 rounded-md bg-slate-100 dark:bg-slate-800" />
                    </div>
                  </div>
                ))}
              </div>
              {/* Financial summary card skeletons */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="min-h-[420px] rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 overflow-hidden animate-pulse">
                    <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3">
                      <div className="h-4 w-2/3 rounded-md bg-slate-200 dark:bg-slate-700" />
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {Array.from({ length: 13 }).map((_, j) => (
                        <div key={j} className="grid grid-cols-[1fr_110px]">
                          <div className="bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
                            <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                          </div>
                          <div className="px-3 py-2 flex justify-end">
                            <div className="h-3 w-4/5 rounded bg-slate-200 dark:bg-slate-700" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showMainLoading && !error && activeTab === 'forecast-report' && forecastDataLoading && (
            <div className="h-[520px]">
              <VisualLoadingCard title="forecast report" />
            </div>
          )}

          {!showMainLoading && !error && activeTab === 'new-stores-report' && newStoresLoading && (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="h-[520px]">
                <VisualLoadingCard title="new stores table" />
              </div>
              <div className="h-[520px]">
                <VisualLoadingCard title="new stores chart" />
              </div>
            </div>
          )}

          {!showMainLoading && !error && !(reportView === 'averages' && averagesLoading) && !(activeTab === 'forecast-report' && forecastDataLoading) && !(activeTab === 'new-stores-report' && newStoresLoading) && (
            <div className="space-y-8">
              {metricWidgets.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {metricWidgets.map((widget) => renderWidget(widget, 'min-h-[120px]'))}
                </div>
              )}

              {summaryWidgets.length > 0 && reportView === 'ratios' ? (
                <div className="space-y-4">
                  {/* Row 1: 4 compact period cards */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {summaryWidgets
                      .filter((w) => !['report-ratio-avg-3', 'report-ratio-avg-12'].includes(w.id))
                      .map((widget) => renderWidget(widget, 'min-h-[280px]'))}
                  </div>
                  {/* Row 2: 3M Avg + 12M Avg + Chart (chart takes remaining 2/4 width) */}
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                    {summaryWidgets
                      .filter((w) => ['report-ratio-avg-3', 'report-ratio-avg-12'].includes(w.id))
                      .map((widget) => {
                        if (averagesLoading) {
                          return (
                            <div key={widget.id} className="min-h-[400px] rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 overflow-hidden animate-pulse">
                              <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
                                <div className="h-4 w-2/3 rounded-md bg-slate-200 dark:bg-slate-700" />
                                <div className="h-3 w-20 rounded-md bg-blue-100 dark:bg-blue-900/40 text-[10px] text-blue-500 dark:text-blue-400 flex items-center justify-center shrink-0">Loading…</div>
                              </div>
                              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                {Array.from({ length: 7 }).map((_, j) => (
                                  <div key={j} className="grid grid-cols-[1fr_110px]">
                                    <div className="bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
                                      <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                    <div className="px-3 py-2 flex justify-end">
                                      <div className="h-3 w-4/5 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return renderWidget(widget, 'min-h-[400px]');
                      })}
                    {chartWidgets.map((widget) => (
                      <div key={widget.id} className="xl:col-span-2 h-[400px]">
                        {renderWidget(widget, 'h-full')}
                      </div>
                    ))}
                  </div>
                </div>
              ) : summaryWidgets.length > 0 && activeTab === 'segment-report' ? (
                <div className="space-y-6">
                  {[
                    { label: 'Mall Type Segment', currentId: 'report-segment-mall-current', avgId: 'report-segment-mall-average' },
                    { label: 'Province Segment', currentId: 'report-segment-province-current', avgId: 'report-segment-province-average' },
                  ].map(({ label, currentId, avgId }) => {
                    const currentWidget = summaryWidgets.find((w) => w.id === currentId);
                    const avgWidget = summaryWidgets.find((w) => w.id === avgId);
                    if (!currentWidget && !avgWidget) return null;
                    return (
                      <div key={label}>
                        <div className="mb-3 flex items-center gap-3">
                          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
                          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                        </div>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                          {currentWidget && segmentDataLoading ? (
                            <div className="min-h-[420px] rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 overflow-hidden animate-pulse">
                              <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
                                <div className="h-4 w-2/3 rounded-md bg-slate-200 dark:bg-slate-700" />
                                <div className="h-3 w-20 rounded-md bg-blue-100 dark:bg-blue-900/40 text-[10px] text-blue-500 dark:text-blue-400 flex items-center justify-center shrink-0">Loading…</div>
                              </div>
                              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                {Array.from({ length: 13 }).map((_, j) => (
                                  <div key={j} className="grid grid-cols-[1fr_110px]">
                                    <div className="bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
                                      <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                    <div className="px-3 py-2 flex justify-end">
                                      <div className="h-3 w-4/5 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : currentWidget ? renderWidget(currentWidget, 'min-h-[420px]') : null}
                          {avgWidget && segmentDataLoading ? (
                            <div className="min-h-[420px] rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 overflow-hidden animate-pulse">
                              <div className="border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
                                <div className="h-4 w-2/3 rounded-md bg-slate-200 dark:bg-slate-700" />
                                <div className="h-3 w-20 rounded-md bg-blue-100 dark:bg-blue-900/40 text-[10px] text-blue-500 dark:text-blue-400 flex items-center justify-center shrink-0">Loading 12M…</div>
                              </div>
                              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                {Array.from({ length: 13 }).map((_, j) => (
                                  <div key={j} className="grid grid-cols-[1fr_110px]">
                                    <div className="bg-slate-50 dark:bg-slate-800/50 px-3 py-2">
                                      <div className="h-3 w-3/4 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                    <div className="px-3 py-2 flex justify-end">
                                      <div className="h-3 w-4/5 rounded bg-slate-200 dark:bg-slate-700" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : avgWidget ? renderWidget(avgWidget, 'min-h-[420px]') : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : summaryWidgets.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-4">
                  {summaryWidgets.map((widget) => renderWidget(widget, 'min-h-[420px]'))}
                </div>
              ) : null}

              {activeTab === 'new-stores-report' && (tableWidgets.length > 0 || chartWidgets.length > 0) ? (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {tableWidgets.map((widget) => renderWidget(widget, 'min-h-[520px]'))}
                  {chartWidgets.map((widget) => renderWidget(widget, 'h-[520px]'))}
                </div>
              ) : reportView !== 'ratios' && chartWidgets.length > 0 && (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {chartWidgets.map((widget) => renderWidget(widget, 'h-96'))}
                </div>
              )}

              {activeTab !== 'new-stores-report' && reportView !== 'ratios' && tableWidgets.length > 0 && (
                <div className="grid grid-cols-1 gap-6">
                  {tableWidgets.map((widget) => renderWidget(widget))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default StorePerformanceDashboard;
